var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import awaitInteractionResponse from 'await-interaction-response';
import { Box, Collapse } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import clsx from 'clsx';
import _debounce from 'lodash-es/debounce';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { jsonLdScriptProps } from 'react-schemaorg';
import { ROUNDELS } from '../../common/props';
import { SiteContext } from '../../utils/common';
import Drawer from '../Drawer/Drawer';
import Icon from '../Icon/Icon';
import Link from '../Link/Link';
import Logo from '../Logo/Logo';
import MegaNavDesktop from '../MegaNavDesktop/MegaNavDesktop';
import MegaNavMobile from '../MegaNavMobile/MegaNavMobile';
import MyAccountMenu from '../MyAccountMenu/MyAccountMenu';
import SearchOverlay from '../SearchOverlay/SearchOverlay';
import SiteUspGallery from '../SiteUspGallery/SiteUspGallery';
import useStyles from './Header.styles';
import Image from '../Image/Image';
import { ACCOUNT, CATEGORY_PATH_PREFIX, CUSTOM_EVENTS, INTERNATIONAL, OVERSEAS_PRODUCT_SLUG_SUFFIX, PRODUCT_PATH_PREFIX, SIGN_IN, STORAGE_KEYS, } from '../../utils/constants';
import DiscountBanner from '../DiscountBanner/DiscountBanner';
import { useLocalStorage } from '../../utils/utilityCustomHooks';
const SCROLL_DEBOUNCE_DELAY_MS = 50;
const SHARED_UK_IE_CONFIG = {
    type: ['Organization', 'Corporation', 'OnlineBusiness'],
    foundingDate: '1923',
    name: 'Interflora',
    description: "Interflora is one of the UK's leading online flower delivery services, supporting local artisan florists across the UK to deliver hand-crafted bouquets and floral gifts for over 100 years.",
    address: {
        '@type': 'PostalAddress',
        streetAddress: 'Interflora House, Watergate',
        addressLocality: 'Sleaford',
        addressRegion: 'Lincolnshire',
        postalCode: 'NG34 7TB',
        addressCountry: 'GB',
    },
    parentOrganization: {
        '@type': 'Corporation',
        name: 'Teleflora LLC',
        sameAs: 'https://en.wikipedia.org/wiki/Teleflora',
    },
    knowsAbout: [
        'Floristry',
        'Flower Delivery',
        'Same Day Flower Delivery',
        'International Flower Delivery',
        'Hand-crafted Bouquets',
        'Sympathy & Funeral Flowers',
        'Birthday Flowers',
        'Artisan Florist Network',
    ],
    logoType: 'image-object',
};
const SCHEMA_CONFIG = {
    uk: Object.assign(Object.assign({}, SHARED_UK_IE_CONFIG), { id: 'https://www.interflora.co.uk/#organization', legalName: 'Interflora British Unit', iso6523Code: '0094:00297087', areaServed: {
            '@type': 'Country',
            name: 'United Kingdom',
            identifier: 'GB',
        }, sameAs: [
            'https://www.facebook.com/InterfloraUK',
            'https://twitter.com/interflorauk/',
            'https://www.youtube.com/user/Interflora/',
            'https://uk.pinterest.com/interflorauk/',
            'https://en.wikipedia.org/wiki/Interflora',
            'https://www.instagram.com/interflorauk/',
            'https://find-and-update.company-information.service.gov.uk/company/00297087',
            'https://uk.linkedin.com/company/interflorauk',
        ], contactType: 'gb' }),
    ie: Object.assign(Object.assign({}, SHARED_UK_IE_CONFIG), { id: 'https://www.interflora.ie/#organization', description: "Interflora is one of Ireland's leading online flower delivery services, supporting local artisan florists across Ireland to deliver hand-crafted bouquets and floral gifts for over 100 years.", areaServed: {
            '@type': 'Country',
            name: 'Ireland',
            identifier: 'IE',
        }, sameAs: [
            'https://www.facebook.com/InterfloraUK',
            'https://twitter.com/interflorauk/',
            'https://www.youtube.com/user/Interflora/',
            'https://www.pinterest.com/interflora/',
            'https://en.wikipedia.org/wiki/Interflora/',
            'https://www.instagram.com/interflorauk/',
        ], contactType: 'ie' }),
    ff: {
        type: 'Organization',
        foundingDate: '1981',
        name: 'Flying Flowers',
        address: {
            '@type': 'PostalAddress',
            addressLocality: 'Sleaford',
            postalCode: 'NG34 7TB',
            streetAddress: 'Watergate',
        },
        sameAs: [
            'https://www.facebook.com/GreatValueFlowers/',
            'https://twitter.com/FlyingFlowersUK',
            'https://www.instagram.com/greatvalueflowers/',
            'https://www.pinterest.co.uk/flyingflowersuk/',
        ],
        contactType: 'ff',
        logoType: 'string',
    },
};
const CONTACT_POINTS = {
    gb: [
        {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            url: 'https://help.interflora.co.uk/hc/en-gb',
            areaServed: 'GB',
            availableLanguage: ['English'],
            description: 'Interflora Customer Help Centre and FAQ Portal',
            telephone: '+44-333-003-3588',
        },
        {
            '@type': 'ContactPoint',
            contactType: 'live chat',
            url: 'https://help.interflora.co.uk/hc/en-gb',
            areaServed: 'GB',
            availableLanguage: ['English'],
            hoursAvailable: {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                opens: '09:00',
                closes: '17:00',
            },
            potentialAction: {
                '@type': 'InteractAction',
                name: 'Start Live Chat',
                target: 'https://help.interflora.co.uk/hc/en-gb',
            },
        },
        {
            '@type': 'ContactPoint',
            telephone: '+44-1529-41-6591',
            contactType: 'sales',
            areaServed: 'GB',
        },
    ],
    ie: [
        {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            telephone: '+3531 686 9394',
            areaServed: 'IE',
            availableLanguage: ['English'],
        },
    ],
    ff: {
        '@type': 'ContactPoint',
        telephone: '+44 0333 003 0855',
        email: 'customerservices@flyingflowers.co.uk',
        contactType: 'customer service',
        areaServed: 'GB',
    },
};
function buildOrganizationSchemaData(shorthand, canonicalHostname) {
    const brand = shorthand === null || shorthand === void 0 ? void 0 : shorthand.toLowerCase();
    const config = SCHEMA_CONFIG[brand];
    if (!config) {
        return { '@context': 'https://schema.org' };
    }
    let logo = `https://${canonicalHostname}/images/logo.svg`;
    if (config.logoType === 'image-object') {
        logo = {
            '@type': 'ImageObject',
            url: `https://${canonicalHostname}/images/logo.svg`,
            caption: 'Interflora Logo',
        };
    }
    let image = undefined;
    if (config.logoType === 'image-object') {
        image = {
            '@type': 'ImageObject',
            url: `https://${canonicalHostname}/favicon.ico`,
            caption: 'Interflora Favicon',
        };
    }
    const contactPoint = CONTACT_POINTS[config.contactType];
    return {
        '@context': 'https://schema.org',
        '@type': config.type,
        '@id': config.id,
        url: `https://${canonicalHostname}`,
        foundingDate: config.foundingDate,
        legalName: config.legalName,
        description: config.description,
        iso6523Code: config.iso6523Code,
        address: config.address,
        name: config.name,
        logo,
        image,
        contactPoint,
        sameAs: config.sameAs,
        parentOrganization: config.parentOrganization,
        areaServed: config.areaServed,
        knowsAbout: config.knowsAbout,
    };
}
const Header = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v;
    const router = useRouter();
    const { getCustomer, canonicalHostname, shorthand, isFlyingFlowers, getConsignmentCount, discountValidationLoading, discountValidationCompleted, } = useContext(SiteContext);
    const { megaNav, title, usps } = props;
    const { menuItems, quickLinksInformation } = megaNav !== null && megaNav !== void 0 ? megaNav : {};
    const { iconPosition, quickLinks } = quickLinksInformation !== null && quickLinksInformation !== void 0 ? quickLinksInformation : {};
    const { signOut, meta, uiType, suggestedSearches, searchDefaultProducts, handleSuggestedSearchOrPreviousSearch, searchTermAnalytics, noSearchOverlayResultAnalytics, sendMenuDrillDownAnalytics, sendMenuLinkClickAnalytics, showLoader, setShowLoader, setPendingSearchQuery, } = props;
    const { siteUspList, showTrustpilotWidget, trustpilotWidgetPosition, transitionDelay, uspColour } = usps !== null && usps !== void 0 ? usps : {};
    const suggestedSearchesData = !!(suggestedSearches === null || suggestedSearches === void 0 ? void 0 : suggestedSearches.length) ? suggestedSearches[0].data : {};
    const { classes } = useStyles();
    const theme = useTheme();
    const isLargerViewport = useMediaQuery({ query: `(min-width: ${theme.breakpoints.values.md}px)` });
    const [isSticky, setSticky] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [showOptions, setShowMenuOption] = useState(false);
    const [showAccountMenu, setShowAccountMenu] = useState(false);
    const [forceDiscountCheck, setForceDiscountCheck] = useState(0);
    const containerRef = useRef(null);
    const path = router === null || router === void 0 ? void 0 : router.asPath;
    const shouldRenderOrganizationSchema = !!canonicalHostname && (path === '/' || (path === null || path === void 0 ? void 0 : path.includes('/about-us')) || (path === null || path === void 0 ? void 0 : path.includes('/contact')));
    const url = '/';
    const { getFromLocalStorage } = useLocalStorage();
    const handleRouteChange = () => {
        setShowSearch(false);
        setShowMenu(false);
        setShowAccountMenu(false);
        setShowMenuOption(false);
    };
    // Listen for discount removal event from basket page
    useEffect(() => {
        const handleDiscountRemoved = () => {
            // Force re-check of discount banner visibility
            setForceDiscountCheck((prev) => prev + 1);
        };
        const handleDiscountReapplied = () => {
            // Force re-check when discount is reapplied
            setForceDiscountCheck((prev) => prev + 1);
        };
        if (globalThis.window !== undefined) {
            globalThis.window.addEventListener(CUSTOM_EVENTS.DISCOUNT_REMOVED, handleDiscountRemoved);
            globalThis.window.addEventListener(CUSTOM_EVENTS.DISCOUNT_REAPPLIED, handleDiscountReapplied);
        }
        return () => {
            if (globalThis.window !== undefined) {
                globalThis.window.removeEventListener(CUSTOM_EVENTS.DISCOUNT_REMOVED, handleDiscountRemoved);
                globalThis.window.removeEventListener(CUSTOM_EVENTS.DISCOUNT_REAPPLIED, handleDiscountReapplied);
            }
        };
    }, []);
    useEffect(() => {
        router === null || router === void 0 ? void 0 : router.events.on('routeChangeStart', handleRouteChange);
        return () => {
            router === null || router === void 0 ? void 0 : router.events.off('routeChangeStart', handleRouteChange);
        };
    }, [router === null || router === void 0 ? void 0 : router.events]);
    useEffect(() => {
        if (isLargerViewport) {
            setSticky(false);
            return;
        }
        const header = document.getElementById('header');
        // The header element itself moves so its offset cant be relied on so use its parent and we need to add the mobile USP height
        const sticky = header.parentElement.offsetTop + 40;
        const handleScroll = _debounce(() => {
            setSticky((prev) => {
                const shouldBecomeSticky = !prev && header.offsetTop > sticky;
                const shouldBecomeUnsticky = prev && globalThis.window.pageYOffset <= sticky;
                if (shouldBecomeSticky)
                    return true;
                if (shouldBecomeUnsticky)
                    return false;
                return prev;
            });
        }, SCROLL_DEBOUNCE_DELAY_MS);
        globalThis.window.addEventListener('scroll', handleScroll);
        return () => {
            globalThis.window.removeEventListener('scroll', handleScroll);
        };
    }, [isLargerViewport]);
    const isTablet = useMediaQuery({ query: `(min-width: ${theme.breakpoints.values.md}px)` });
    useEffect(() => {
        if (isTablet)
            setShowMenu(false);
        if (showMenu) {
            globalThis.window.document.body.style.overflow = 'hidden';
            globalThis.window.document.body.style.touchAction = 'none';
        }
        else {
            globalThis.window.document.body.style.overflow = 'unset';
            globalThis.window.document.body.style.touchAction = 'unset';
        }
    }, [showMenu, isTablet]);
    useEffect(() => {
        const outsideClick = (event) => {
            var _a;
            if (showOptions && !((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
                setShowMenuOption(false);
            }
        };
        document.body.addEventListener('click', outsideClick, true);
        return () => {
            document.body.removeEventListener('click', outsideClick, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOptions]);
    useEffect(() => {
        if (showMenu || showSearch || showAccountMenu) {
            document.body.style.overflow = 'hidden';
            document.body.style.touchAction = 'none';
        }
        else {
            document.body.style.overflow = 'unset';
            document.body.style.touchAction = 'unset';
        }
    }, [showMenu, showSearch, showAccountMenu]);
    // UK2IE/IE2UK ('-int' slug) product pages don't otherwise carry an "international" marker in
    // the URL or title, so the slug suffix is the only reliable signal for them.
    const isOverseasProductPath = useMemo(() => {
        var _a, _b, _c;
        const pathname = (_c = (_b = (_a = router === null || router === void 0 ? void 0 : router.asPath) === null || _a === void 0 ? void 0 : _a.split(/[?#]/)[0]) === null || _b === void 0 ? void 0 : _b.toLowerCase()) !== null && _c !== void 0 ? _c : '';
        return pathname.startsWith(PRODUCT_PATH_PREFIX) && pathname.endsWith(OVERSEAS_PRODUCT_SLUG_SUFFIX);
    }, [router === null || router === void 0 ? void 0 : router.asPath]);
    const shouldShowHomeIcon = useMemo(() => {
        var _a;
        const isInternationalInUrl = (_a = router === null || router === void 0 ? void 0 : router.asPath) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(INTERNATIONAL);
        const isInternationalInTitle = title === null || title === void 0 ? void 0 : title.toLowerCase().includes(INTERNATIONAL);
        return !!isInternationalInUrl || !!isInternationalInTitle || isOverseasProductPath;
    }, [router === null || router === void 0 ? void 0 : router.asPath, title, isOverseasProductPath]);
    // Search is hidden across the International journey, including UK2IE/IE2UK product pages, so
    // customers can't cross back into the Domestic journey from it.
    const shouldHideSearchIcon = useMemo(() => {
        var _a, _b, _c;
        if (isFlyingFlowers)
            return false;
        const pathname = (_c = (_b = (_a = router === null || router === void 0 ? void 0 : router.asPath) === null || _a === void 0 ? void 0 : _a.split(/[?#]/)[0]) === null || _b === void 0 ? void 0 : _b.toLowerCase()) !== null && _c !== void 0 ? _c : '';
        const hasInternationalTitle = !!(title === null || title === void 0 ? void 0 : title.toLowerCase().includes(INTERNATIONAL));
        if (pathname.startsWith(PRODUCT_PATH_PREFIX)) {
            return isOverseasProductPath || hasInternationalTitle;
        }
        if (pathname.startsWith(CATEGORY_PATH_PREFIX)) {
            return pathname.split('/').includes(INTERNATIONAL) || hasInternationalTitle;
        }
        return false;
    }, [isFlyingFlowers, router === null || router === void 0 ? void 0 : router.asPath, title, isOverseasProductPath]);
    const handleShowMenu = () => __awaiter(void 0, void 0, void 0, function* () {
        yield awaitInteractionResponse();
        setShowSearch(false);
        setShowMenu(!showMenu);
        setShowAccountMenu(false);
    });
    const handleShowSearch = () => __awaiter(void 0, void 0, void 0, function* () {
        yield awaitInteractionResponse();
        setShowMenu(false);
        setShowSearch(!showSearch);
        setShowAccountMenu(false);
    });
    const handleSetShowMobileMenuOptions = () => {
        setShowAccountMenu(!showAccountMenu);
        setShowMenu(false);
        setShowSearch(false);
    };
    const handleLinkSelection = (label) => {
        setShowMenu(false);
        sendMenuLinkClickAnalytics === null || sendMenuLinkClickAnalytics === void 0 ? void 0 : sendMenuLinkClickAnalytics(label);
    };
    const showMenuFF = showMenu ? `/images/icons/headerIcons/ff-close.svg` : `/images/icons/headerIcons/ff-menu.svg`;
    const showMenuUK = showMenu ? `/images/icons/headerIcons/close.svg` : `/images/icons/headerIcons/hamburger.svg`;
    const showSearchFF = showSearch ? `/images/icons/headerIcons/ff-close.svg` : `/images/icons/headerIcons/ff-search.svg`;
    const showSearchUK = showSearch ? `/images/icons/headerIcons/close.svg` : `/images/icons/headerIcons/search.svg`;
    const getUKMobileIcon = showAccountMenu
        ? `/images/icons/headerIcons/close.svg`
        : `/images/icons/headerIcons/account.svg`;
    const getFFMobileIcon = showAccountMenu
        ? `/images/icons/headerIcons/ff-close.svg`
        : `/images/icons/headerIcons/ff-account.svg`;
    const homeIcon = `/images/icons/headerIcons/home.svg`;
    const guestMobileIcon = (_jsxs("button", { className: clsx(classes.svpControls, classes.iconContainer, showAccountMenu && classes.close), onClick: handleSetShowMobileMenuOptions, children: [_jsx(Image, { imageUrl: isFlyingFlowers ? getFFMobileIcon : getUKMobileIcon, altText: showAccountMenu ? 'Close' : 'Account', priority: true, height: isFlyingFlowers ? 40 : 32, width: isFlyingFlowers ? 40 : 32 }, showAccountMenu ? 'close' : 'account'), _jsx("div", { children: showAccountMenu ? 'Close' : 'Account' })] }));
    const renderSiteUSPGallery = () => {
        var _a;
        if ((siteUspList === null || siteUspList === void 0 ? void 0 : siteUspList.length) || showTrustpilotWidget) {
            const { textColour = '#000000', backgroundColour = '#F6F6F6' } = ((_a = uspColour === null || uspColour === void 0 ? void 0 : uspColour.backgroundAndTextColours) === null || _a === void 0 ? void 0 : _a[0]) || {};
            const textColorStyle = textColour ? { color: textColour } : undefined;
            const backgroundColorStyle = backgroundColour ? { backgroundColor: backgroundColour } : undefined;
            return (_jsx("div", { className: classes.uspContainer, style: backgroundColorStyle !== null && backgroundColorStyle !== void 0 ? backgroundColorStyle : undefined, children: _jsx(SiteUspGallery, { siteUspList: siteUspList || [], showTrustpilotWidget: showTrustpilotWidget, trustpilotWidgetPosition: trustpilotWidgetPosition, transitionDelay: transitionDelay, textColorStyle: textColorStyle, backgroundColorStyle: backgroundColorStyle }) }));
        }
        else {
            return null;
        }
    };
    // Check if discount banner should be shown (re-check when forceDiscountCheck changes)
    const shouldShowDiscountBanner = () => {
        var _a;
        // Force a re-check by using forceDiscountCheck in dependency
        const discountData = getFromLocalStorage(STORAGE_KEYS.DISCOUNT_BY_URL);
        const ctdKeyFromStorage = getFromLocalStorage(STORAGE_KEYS.CTD_KEY);
        // Get ctdKey from URL
        let ctdKey = null;
        if (globalThis.window !== undefined) {
            const params = new URLSearchParams((_a = globalThis === null || globalThis === void 0 ? void 0 : globalThis.location) === null || _a === void 0 ? void 0 : _a.search);
            ctdKey = params.get('ctdKey');
        }
        return (ctdKeyFromStorage &&
            (ctdKeyFromStorage === (discountData === null || discountData === void 0 ? void 0 : discountData.ctdKey) || !ctdKey || ctdKey === '') &&
            (discountData === null || discountData === void 0 ? void 0 : discountData.code) &&
            (discountData === null || discountData === void 0 ? void 0 : discountData.isApplied) !== false // Show banner unless explicitly set to false
        );
    };
    const showDiscountBanner = shouldShowDiscountBanner();
    const renderUSPOrDiscountBanner = () => {
        // Show loading state or when discount is not yet applied
        if (discountValidationLoading && !discountValidationCompleted) {
            return renderSiteUSPGallery();
        }
        // Show discount banner if conditions are met (updates when forceDiscountCheck changes)
        if (showDiscountBanner) {
            return (_jsx("div", { className: classes.uspContainer, children: _jsx(DiscountBanner, {}) }, forceDiscountCheck));
        }
        // Default: Show USP gallery
        return renderSiteUSPGallery();
    };
    const loggedInMobileIcon = (_jsx("a", { className: clsx(classes.svpLink, classes.iconContainer), href: '/sign-in', children: _jsxs("button", { className: classes.svpControls, children: [_jsx(Image, { imageUrl: isFlyingFlowers ? `/images/icons/headerIcons/ff-account.svg` : `/images/icons/headerIcons/account.svg`, altText: (getCustomer === null || getCustomer === void 0 ? void 0 : getCustomer.loading) ? ' ' : 'Sign in', priority: true, height: isFlyingFlowers ? 40 : 32, width: isFlyingFlowers ? 40 : 32 }, (getCustomer === null || getCustomer === void 0 ? void 0 : getCustomer.loading) ? ' ' : 'Sign in'), _jsx("div", { children: (getCustomer === null || getCustomer === void 0 ? void 0 : getCustomer.loading) ? ' ' : 'Sign in' })] }) }));
    return (_jsxs(_Fragment, { children: [_jsxs("header", { id: "header", className: clsx(classes.header, isSticky ? 'condensed' : '', !(siteUspList === null || siteUspList === void 0 ? void 0 : siteUspList.length) && !showTrustpilotWidget ? classes.stickyHeader : ''), "data-type": uiType || 'header', "data-id": meta === null || meta === void 0 ? void 0 : meta.deliveryId, "data-hj-ignore-attributes": true, children: [_jsxs("div", { className: classes.headerContent, children: [renderUSPOrDiscountBanner(), _jsx("div", { className: clsx(classes.controlContainer, isSticky && classes.sticky), children: _jsxs("div", { className: clsx(classes.controlContainerInner), children: [!isFlyingFlowers && shouldShowHomeIcon ? (_jsx("div", { className: clsx(classes.svpControls), "data-testid": `MobileHomeId`, children: _jsxs("button", { className: clsx(classes.svpControls), onClick: () => {
                                                    void router.push(url);
                                                }, children: [_jsx(Image, { imageUrl: homeIcon, altText: 'Home', priority: true, height: 32, width: 32 }, 'MobileHomeId'), _jsx("div", { children: 'Home' })] }) })) : (_jsx("div", { "data-testid": showMenu ? 'Close Menu' : 'Open Menu', children: _jsxs("button", { className: clsx(classes.svpControls, showMenu && classes.close), onClick: handleShowMenu, children: [_jsx(Image, { imageUrl: isFlyingFlowers ? showMenuFF : showMenuUK, altText: showMenu ? 'close' : 'menu', priority: true, height: isFlyingFlowers ? 40 : 32, width: isFlyingFlowers ? 40 : 32 }, showMenu ? 'close' : 'menu'), _jsx("div", { children: showMenu ? 'Close' : 'Menu' })] }) })), shouldHideSearchIcon ? (_jsx("div", { className: classes.searchSpacer, "data-testid": "SearchSpacer", "aria-hidden": "true" })) : (_jsx("div", { "data-testid": showSearch ? 'Close' : 'Search', children: _jsxs("button", { className: clsx(classes.svpControls, showSearch && classes.close), onClick: handleShowSearch, children: [_jsx(Image, { imageUrl: isFlyingFlowers ? showSearchFF : showSearchUK, altText: showSearch ? 'Close' : 'Search', priority: true, height: isFlyingFlowers ? 40 : 32, width: isFlyingFlowers ? 40 : 32 }, showSearch ? 'close' : 'search'), _jsx("div", { children: showSearch ? 'Close' : 'Search' })] }) })), _jsx(Logo, { url: url, condensed: isSticky, className: classes.logo }), _jsx("div", { "data-testid": !((_b = (_a = getCustomer === null || getCustomer === void 0 ? void 0 : getCustomer.data) === null || _a === void 0 ? void 0 : _a.customer) === null || _b === void 0 ? void 0 : _b.id) ? SIGN_IN : ACCOUNT, children: !((_d = (_c = getCustomer === null || getCustomer === void 0 ? void 0 : getCustomer.data) === null || _c === void 0 ? void 0 : _c.customer) === null || _d === void 0 ? void 0 : _d.id) ? loggedInMobileIcon : guestMobileIcon }), _jsxs("div", { className: clsx(classes.shoppingCartContainer, classes.hideBasket, classes.mobileShoppingCartContainer), "data-testid": `Basket`, children: [_jsxs("button", { className: classes.svpControls, "aria-label": isFlyingFlowers ? 'ff-Basket' : 'uk-Basket', onClick: () => {
                                                        void router.push('/basket');
                                                    }, children: [_jsx(Image, { imageUrl: isFlyingFlowers
                                                                ? `/images/icons/headerIcons/ff-basket.svg`
                                                                : `/images/icons/headerIcons/basket.svg`, altText: `Basket`, priority: true, height: isFlyingFlowers ? 40 : 32, width: isFlyingFlowers ? 40 : 32 }, `Basket`), _jsx("div", { children: `Basket` })] }), ((_e = getConsignmentCount === null || getConsignmentCount === void 0 ? void 0 : getConsignmentCount.data) === null || _e === void 0 ? void 0 : _e.cart) && ((_g = (_f = getConsignmentCount === null || getConsignmentCount === void 0 ? void 0 : getConsignmentCount.data) === null || _f === void 0 ? void 0 : _f.cart) === null || _g === void 0 ? void 0 : _g.totalConsignments) != 0 && (_jsx("div", { className: clsx(classes.loginAndAccountIcon, classes.itemsCount, classes.mobileItemsCount), children: (_j = (_h = getConsignmentCount === null || getConsignmentCount === void 0 ? void 0 : getConsignmentCount.data) === null || _h === void 0 ? void 0 : _h.cart) === null || _j === void 0 ? void 0 : _j.totalConsignments }))] }), _jsxs("div", { className: classes.lvpNavContainer, children: [!!title || isOverseasProductPath ? (_jsx("div", { className: classes.title, children: title })) : !!(menuItems === null || menuItems === void 0 ? void 0 : menuItems.length) ? (_jsx(MegaNavDesktop, { menuItems: menuItems, sendMenuDrillDownAnalytics: sendMenuDrillDownAnalytics, sendMenuLinkClickAnalytics: sendMenuLinkClickAnalytics })) : (_jsx("div", {})), _jsxs("div", { className: classes.lvpControls, children: [!isFlyingFlowers && shouldShowHomeIcon && (_jsx("div", { className: classes.shoppingCartContainer, "data-testid": `Home`, children: _jsx(Icon, { icon: 'home', onClick: () => {
                                                                    void router.push(url);
                                                                }, className: classes.icon }) })), !shouldHideSearchIcon && (_jsx("div", { className: classes.action, "data-testid": showSearch ? 'Close' : 'Search', children: _jsx(Icon, { icon: showSearch ? 'close' : 'search', ariaLabel: "Search", onClick: () => handleShowSearch(), className: clsx(classes.icon, showSearch && classes.close), roundel: isFlyingFlowers ? ROUNDELS.ROUNDEL_1 : undefined }) })), _jsx("div", { "data-testid": !((_l = (_k = getCustomer === null || getCustomer === void 0 ? void 0 : getCustomer.data) === null || _k === void 0 ? void 0 : _k.customer) === null || _l === void 0 ? void 0 : _l.id) ? SIGN_IN : ACCOUNT, children: !((_o = (_m = getCustomer === null || getCustomer === void 0 ? void 0 : getCustomer.data) === null || _m === void 0 ? void 0 : _m.customer) === null || _o === void 0 ? void 0 : _o.id) ? (_jsx(Link, { url: "/sign-in", icon: "person", roundel: isFlyingFlowers ? ROUNDELS.ROUNDEL_1 : undefined, label: '', className: classes.anonymousUser })) : (_jsxs("div", { className: clsx(classes.shoppingCartContainer, classes.shopCart, classes.action), children: [_jsx(Icon, { label: ``, icon: "person", className: clsx(showOptions ? classes.actionActive : '', classes.icon), onClick: () => setShowMenuOption(!showOptions), roundel: isFlyingFlowers ? ROUNDELS.ROUNDEL_1 : undefined }), ((_q = (_p = getCustomer === null || getCustomer === void 0 ? void 0 : getCustomer.data) === null || _p === void 0 ? void 0 : _p.customer) === null || _q === void 0 ? void 0 : _q.id) ? (_jsx("div", { className: clsx(classes.itemsCount, classes.loginAndAccountIcon, classes.dekstopItemCount), children: _jsx(Icon, { icon: 'done', className: classes.loggedInIcon, onClick: () => setShowMenuOption(!showOptions) }) })) : null, showOptions ? (_jsx("div", { className: classes.menuOptions, ref: containerRef, children: _jsx(MyAccountMenu, { signOut: signOut }) })) : null] })) }), _jsxs("div", { className: classes.shoppingCartContainer, "data-testid": `Basket`, children: [_jsx(Icon, { icon: isFlyingFlowers ? 'shopping_basket' : 'shopping_cart', onClick: () => {
                                                                        void router.push('/basket');
                                                                    }, className: classes.icon, roundel: isFlyingFlowers ? ROUNDELS.ROUNDEL_1 : undefined }), ((_r = getConsignmentCount === null || getConsignmentCount === void 0 ? void 0 : getConsignmentCount.data) === null || _r === void 0 ? void 0 : _r.cart) && ((_t = (_s = getConsignmentCount === null || getConsignmentCount === void 0 ? void 0 : getConsignmentCount.data) === null || _s === void 0 ? void 0 : _s.cart) === null || _t === void 0 ? void 0 : _t.totalConsignments) != 0 && (_jsx("div", { className: clsx(classes.itemsCount, classes.dekstopItemCount), children: (_v = (_u = getConsignmentCount === null || getConsignmentCount === void 0 ? void 0 : getConsignmentCount.data) === null || _u === void 0 ? void 0 : _u.cart) === null || _v === void 0 ? void 0 : _v.totalConsignments }))] })] })] })] }) })] }), !!(menuItems === null || menuItems === void 0 ? void 0 : menuItems.length) && (_jsx(Box, { sx: { display: { xs: 'block', md: 'none' } }, children: _jsx(Drawer, { open: showMenu, onClose: () => setShowMenu(false), setOpen: () => setShowMenu(true), title: "Menu", isFromHeader: true, condensed: isSticky, children: _jsx(Collapse, { in: showMenu, className: classes.collapse, unmountOnExit: true, children: _jsx(MegaNavMobile, { menuItems: menuItems, onLinkSelection: handleLinkSelection, iconPosition: iconPosition, quickLinks: quickLinks, sendMenuDrillDownAnalytics: sendMenuDrillDownAnalytics }) }) }) })), _jsx(Collapse, { in: showSearch, className: classes.collapse, unmountOnExit: true, children: _jsx(SearchOverlay, { showSearch: showSearch, suggestedSearches: suggestedSearchesData, searchDefaultProducts: searchDefaultProducts, handleSuggestedSearchOrPreviousSearch: handleSuggestedSearchOrPreviousSearch, searchTermAnalytics: searchTermAnalytics, noSearchOverlayResultAnalytics: noSearchOverlayResultAnalytics, showLoader: showLoader, setShowLoader: setShowLoader, setPendingSearchQuery: setPendingSearchQuery, setShowSearch: setShowSearch }) }), _jsx(Box, { sx: { display: { xs: 'block', md: 'none' } }, children: _jsx(Collapse, { in: showAccountMenu, className: classes.collapse, children: _jsx(MyAccountMenu, { signOut: signOut }) }) })] }), shouldRenderOrganizationSchema ? (_jsx(Head, { children: _jsx("script", Object.assign({}, jsonLdScriptProps(buildOrganizationSchemaData(shorthand, canonicalHostname)))) })) : null] }));
};
export default Header;
